import { getDocs } from '@cloudio-saas/quickx';

const FunctionTypes = {
  String: 'String',
  Number: 'Number',
  Boolean: 'Boolean',
  Date: 'Date',
  Custom: 'Custom',
};
export type FUNCTION = {
  name: string;
  displayName: string;
  minParams?: number;
  maxParams?: number;
  usage: string;
  example: string;
  description: string;
};

export type PROPERTY = {
  name: string;
  displayName?: string;
  usage: string;
  description: string;
};

export const CATEGORIES = {
  ...FunctionTypes,
  operator: 'Operator',
  session: 'Session',
  screen: 'Screen',
  itemProperty: 'Item Property',
  dataSource: 'Data Source',
  dynamic: 'Dynamic Data',
  theme: 'Theme',
  attachmentData: 'Attachment Data',
  other: 'Other',
};

const operator: { [key: string]: PROPERTY } = {};
[
  '!=',
  '!==',
  '%',
  '&&',
  '*',
  '+',
  '-',
  '/',
  '<',
  '<=',
  '==',
  '===',
  '>',
  '>=',
  '??',
  '^',
  '||',
].forEach((key) => {
  operator[key] = { name: key, usage: ` ${key} `, description: key };
});

export const FUNCTIONS: {
  [category: string]: { [key: string]: FUNCTION | PROPERTY };
} = {
  operator,
  Number: {
    'Math.max': {
      name: 'Math.max',
      description:
        "The Math.max() function returns the largest of the zero or more numbers given as input parameters, or NaN if any parameter isn't a number and can't be converted into one.",
      usage: 'Math.max(param1, param2, param3)',
      example: 'Math.max(1, 2, 3)',
    },
    'Math.min': {
      name: 'Math.min',
      description:
        'The Math.min() static method returns the smallest of the numbers given as input parameters, or Infinity if there are no parameters.',
      usage: 'Math.min(param1, param2, ?param3)',
      example: 'Same as Math.min',
    },
    'Math.abs': {
      name: 'Math.abs',
      description:
        'The Math.abs() function returns the absolute value of a number. That is, it returns x if x is positive or zero, and the negation of x if x is negative.',
      usage: 'Math.abs(param1)',
      example: 'Math.abs(-1)',
    },
    'Math.round': {
      name: 'Math.round',
      description:
        'The Math.round() function returns the value of a number rounded to the nearest integer.',
      usage: 'Math.round(param1)',
      example: 'Math.round(1.01)',
    },
    'Math.trunc': {
      name: 'Math.trunc',
      description:
        'The Math.trunc() function returns the integer part of a number by removing any fractional digits.',
      usage: 'Math.trunc(param1)',
      example: 'Math.trunc(1.01)',
    },
    'Math.ceil': {
      name: 'Math.ceil',
      description:
        'The Math.ceil() function always rounds a number up to the next largest integer.',
      usage: 'Math.ceil(param1)',
      example: 'Math.ceil(1.91)',
    },
    'Math.floor': {
      name: 'Math.floor',
      description:
        'The Math.floor() function returns the largest integer less than or equal to a given number.',
      usage: 'Math.floor(param1)',
      example: 'Math.floor(1.91)',
    },
    'Math.random': {
      name: 'Math.random',
      description:
        'Generates a pseudorandom number greater than or equal to 0.0 and less than 1.0.',
      usage: 'Math.random()',
      example: 'Math.random()',
    },
    'Math.sqrt': {
      name: 'Math.sqrt',
      description:
        'The Math.sqrt() static method returns the square root of a number.',
      usage: 'Math.sqrt(param1)',
      example: 'Same as Math.sqrt',
    },
    'Math.tan': {
      name: 'Math.tan',
      description:
        'The Math.tan() static method returns the tangent of a number in radians.',
      usage: 'Math.tan(param1)',
      example: 'Same as Math.tan',
    },
    'Math.tanh': {
      name: 'Math.tanh',
      description:
        'The Math.tanh() static method returns the hyperbolic tangent of a number. ',
      usage: 'Math.tanh(param1)',
      example: 'Same as Math.tanh',
    },
    'Math.sin': {
      name: 'Math.sin',
      description:
        'The Math.sin() static method returns the sine of a number in radians.',
      usage: 'Math.sin(param1)',
      example: 'Same as Math.sin',
    },
    'Math.sinh': {
      name: 'Math.sinh',
      description:
        'The Math.sinh() static method returns the hyperbolic sine of a number.',
      usage: 'Math.sinh(param1)',
      example: 'Same as Math.sinh',
    },
    'Math.cos': {
      name: 'Math.cos',
      description:
        'The Math.cos() static method returns the cosine of a number in radians.',
      usage: 'Math.cos(param1)',
      example: 'Same as Math.cos',
    },
    'Math.cosh': {
      name: 'Math.cosh',
      description:
        'The Math.cosh() static method returns the hyperbolic cosine of a number.',
      usage: 'Math.cosh(param1)',
      example: 'Same as Math.cosh',
    },
    'Math.log10': {
      name: 'Math.log10',
      description:
        'The Math.log10() static method returns the base 10 logarithm of a number.',
      usage: 'Math.log10(param1)',
      example: 'Same as Math.log10',
    },
    'Math.log': {
      name: 'Math.log',
      description:
        'The Math.log() static method returns the natural logarithm (base e) of a number.',
      usage: 'Math.log(param1)',
      example: 'Same as Math.log',
    },
  },
  screen: {
    height: {
      name: 'height',
      description: 'Screen height',
      usage: '$.screen.height',
    },
    width: {
      name: 'width',
      description: 'Screen width',
      usage: '$.screen.width',
    },
    lg: {
      name: 'lg',
      description: 'Is Screen Large i.e. >= 992px & < 1200px',
      usage: '$.screen.lg',
    },
    md: {
      name: 'md',
      description: 'Is Screen Medium i.e. >= 768px & < 992px',
      usage: '$.screen.md',
    },
    xl: {
      name: 'xl',
      description: 'Is Screen extra Large i.e. >= 1200px',
      usage: '$.screen.xl',
    },
    portrait: {
      name: 'portrait',
      description: 'Is Screen in portrait mode i.e. height > width',
      usage: '$.screen.portrait',
    },
    landscape: {
      name: 'landscape',
      description: 'Is Screen in landscape mode i.e. width > height',
      usage: '$.screen.landscape',
    },
    mobile: {
      name: 'mobile',
      description: 'Is display in mobile mode i.e. < 768px',
      usage: '$.screen.mobile',
    },
    tablet: {
      name: 'tablet',
      description: 'Is Screen in tablet mode i.e. >= 768px & < 992px',
      usage: '$.screen.tablet',
    },
  },
  session: {
    displayName: {
      name: 'displayName',
      description: 'Display Name',
      usage: '$.session.displayName',
    },
    emailAddress: {
      name: 'emailAddress',
      description: 'Email Address',
      usage: '$.session.emailAddress',
    },
    isAuthenticated: {
      name: 'isAuthenticated',
      description: 'Is User Authenticated',
      usage: '$.session.isAuthenticated',
    },
    otherAttributes: {
      name: 'otherAttributes',
      description:
        'Access Session Other Attributes, It returns all attributes in json form. Use _ to access the attributes. Ex: $.session.otherAttributes_attributeName',
      usage: '$.session.otherAttributes',
      example: '$.session.otherAttributes_attributeName',
    },
    orgUid: {
      name: 'orgUid',
      description: 'Org UId',
      usage: '$.session.orgUid',
    },
    appUid: {
      name: 'appUid',
      description: 'Current App UId',
      usage: '$.session.appUid',
    },
    pageId: {
      name: 'pageId',
      description: 'Current Root PageId',
      usage: '$.session.pageId',
    },
    sessionId: {
      name: 'sessionId',
      description: 'Session Id',
      usage: '$.session.sessionId',
    },
    role: {
      name: 'role',
      description: 'Current Role Code',
      usage: '$.session.role',
    },
    userName: {
      name: 'userName',
      description: 'User Name',
      usage: '$.session.userName',
    },
    userId: {
      name: 'userId',
      description: 'Current User Id',
      usage: '$.session.userId',
    },
  },
  dynamic: {
    chartTooltipData: {
      name: 'chartTooltipData',
      description:
        'Access chart tooltip data that is set when the user mouse hovers on the chart.',
      usage: '$.$chartTooltipData.VARIABLE_NAME',
      example: '$.$chartTooltipData.isActive',
    },
    pageData: {
      name: 'pageData',
      description: 'Access page data that is set using setData action.',
      usage: '$.$pageData.VARIABLE_NAME',
      example: '$.$pageData.isActive',
    },
    globalData: {
      name: 'globalData',
      description: 'Access global data that is set using setData action.',
      usage: '$.$globalData.VARIABLE_NAME',
      example: '$.$globalData.isOnline',
    },
    localStorage: {
      name: 'localStorage',
      description: 'Access localStorage data that is set using setData action.',
      usage: '$.$localStorage.VARIABLE_NAME',
      example: '$.$localStorage.isOnline',
    },
    param: {
      name: 'param',
      description: 'Access URL param values.',
      usage: '$.$param.PARAM_NAME',
      example: '$.$param.ticketId',
    },
    serverResponse: {
      name: 'serverResponse',
      description:
        'Access the response return from the cloud functions. Can be used in the actions under Invoke Cloud Function action.',
      usage: '$._.response?.VARIABLE_NAME',
      example: '$._.response.status',
    },
  },
  theme: {
    mode: {
      name: 'mode',
      description: "Access the theme's mode. Will return dark or light.",
      usage: '$.theme.palette.mode',
      example: '$.theme.palette.mode',
    },
  },
  attachmentData: {
    fileUid: {
      name: 'fileUid',
      description:
        'Access fileUid of a attachment uploaded using attachment field. Only accessible under TriggerOnChange action of attachment field',
      usage: '$._.file.fileUid',
    },
    fileName: {
      name: 'fileName',
      description:
        'Access fileName of a attachment uploaded using attachment field. Only accessible under TriggerOnChange action of attachment field',
      usage: '$._.file.fileName',
    },
    fileVersion: {
      name: 'fileVersion',
      description:
        'Access fileVersion of a attachment uploaded using attachment field. Only accessible under TriggerOnChange action of attachment field',
      usage: '$._.file.fileVersion',
    },
    fileSize: {
      name: 'fileSize',
      description:
        'Access fileSize of a attachment uploaded using attachment field. Only accessible under TriggerOnChange action of attachment field',
      usage: '$._.file.fileSize',
    },
    contentType: {
      name: 'contentType',
      description:
        'Access contentType of a attachment uploaded using attachment field. Only accessible under TriggerOnChange action of attachment field',
      usage: '$._.file.contentType',
    },
  },
};

['primary', 'secondary', 'success', 'warning', 'error'].forEach((palette) => {
  ['main', 'dark', 'light', 'contrastText'].forEach((type) => {
    FUNCTIONS.theme[`${palette}.${type}`] = {
      name: `${palette}.${type}`,
      description: `Returns ${palette} palette's ${type} color.`,
      usage: `$.theme.palette.${palette}.${type}`,
      example: `$.theme.palette.${palette}.${type}`,
    };
  });
});

['background'].forEach((palette) => {
  ['paper', 'default'].forEach((type) => {
    FUNCTIONS.theme[`${palette}.${type}`] = {
      name: `${palette}.${type}`,
      description: `Returns ${palette} ${type} color.`,
      usage: `$.theme.palette.${palette}.${type}`,
      example: `$.theme.palette.${palette}.${type}`,
    };
  });
});

['text'].forEach((palette) => {
  ['primary', 'secondary'].forEach((type) => {
    FUNCTIONS.theme[`${palette}.${type}`] = {
      name: `${palette}.${type}`,
      description: `Returns ${palette} ${type} color.`,
      usage: `$.theme.palette.${palette}.${type}`,
      example: `$.theme.palette.${palette}.${type}`,
    };
  });
});

['common'].forEach((palette) => {
  ['black', 'white'].forEach((type) => {
    FUNCTIONS.theme[`${palette}.${type}`] = {
      name: `${palette}.${type}`,
      description: `Returns ${palette} ${type} color.`,
      usage: `$.theme.palette.${palette}.${type}`,
      example: `$.theme.palette.${palette}.${type}`,
    };
  });
});

['action'].forEach((palette) => {
  ['selected', 'hover', 'active', 'disabled', 'focus'].forEach((type) => {
    FUNCTIONS.theme[`${palette}.${type}`] = {
      name: `${palette}.${type}`,
      description: `Returns ${palette} ${type} color.`,
      usage: `$.theme.palette.${palette}.${type}`,
      example: `$.theme.palette.${palette}.${type}`,
    };
  });
});

['divider'].forEach((type) => {
  FUNCTIONS.theme[`${type}`] = {
    name: `${type}`,
    description: `Returns palette's ${type} color.`,
    usage: `$.theme.palette.${type}`,
    example: `$.theme.palette.${type}`,
  };
});

export function initFunctionDocs() {
  getDocs().forEach((cfn) => {
    let funcs = FUNCTIONS[cfn.category];
    if (!funcs) {
      funcs = {};
      FUNCTIONS[cfn.category] = funcs;
    }
    funcs[cfn.name] = {
      name: cfn.name,
      description: cfn.description,
      usage: cfn.usage,
      example: cfn.example,
    };
  });
}

export const getFunctionsByCategory = (category: string) => {
  const funcs = FUNCTIONS[category];
  if (funcs) {
    return Object.keys(funcs)
      .map((key) => funcs[key].name)
      .sort();
  }
  return [];
};

export const categoriesArr = Object.keys(CATEGORIES).sort();
export const getCategoryLabel = (category: string) => {
  return CATEGORIES[category as keyof typeof CATEGORIES];
};

export const getFunctionLabel = (category: string, name: string) => {
  const func = getFunction(category, name);
  return func.displayName || func.name;
};

export const getFunction = (category: string, name: string) => {
  return FUNCTIONS[category]?.[name];
};
